import { defineChain, fallback } from "viem";
import { http, createConfig } from "wagmi";
import { coinbaseWallet, injected, walletConnect } from "wagmi/connectors";

export const robinhood = defineChain({
  id: 4663,
  name: "Robinhood Chain",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: { http: ["https://rpc.mainnet.chain.robinhood.com"] },
  },
  blockExplorers: {
    default: {
      name: "Blockscout",
      url: "https://robinhoodchain.blockscout.com",
    },
  },
});

export const robinhoodTestnet = defineChain({
  id: 46630,
  name: "Robinhood Chain Testnet",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: { http: ["https://rpc.testnet.chain.robinhood.com"] },
  },
  blockExplorers: {
    default: {
      name: "Blockscout",
      url: "https://explorer.testnet.chain.robinhood.com",
    },
  },
});

const chainId = Number(import.meta.env.VITE_CHAIN_ID || 4663);
export const activeChain = chainId === 46630 ? robinhoodTestnet : robinhood;

export const COLOR_WHEEL_ADDRESS = (import.meta.env.VITE_COLOR_WHEEL_ADDRESS ||
  "0x0000000000000000000000000000000000000000") as `0x${string}`;

export const WHEEL_TOKEN_ADDRESS = (import.meta.env.VITE_WHEEL_TOKEN_ADDRESS ||
  "0x0000000000000000000000000000000000000000") as `0x${string}`;

export const contractsConfigured =
  COLOR_WHEEL_ADDRESS !== "0x0000000000000000000000000000000000000000" &&
  WHEEL_TOKEN_ADDRESS !== "0x0000000000000000000000000000000000000000";

/**
 * WalletConnect project id — free from cloud.reown.com. Without it only
 * browser-extension and Coinbase wallets are offered; with it, any mobile
 * wallet (Robinhood Wallet, Rainbow, Trust, Zerion…) can pair by QR code.
 */
const WC_PROJECT_ID = (import.meta.env.VITE_WALLETCONNECT_PROJECT_ID as string | undefined) ?? "";

export const walletConnectAvailable = Boolean(WC_PROJECT_ID);

const appMetadata = {
  name: "WHEEL",
  description: "Provably fair colour wheel on Robinhood Chain — true 10×, zero house edge.",
  url: typeof window !== "undefined" ? window.location.origin : "https://wheel.game",
  icons: [
    typeof window !== "undefined" ? `${window.location.origin}/favicon.svg` : "",
  ].filter(Boolean),
};

// RPC the site reads from. The public chain RPC rate-limits browser polling,
// which makes live values (treasury, max bet…) flicker. In production we read
// through the same-origin `/rpc` proxy in server.mjs, which forwards to a
// reliable upstream (ROBINHOOD_RPC_URL) and keeps its key off the client.
// Override with VITE_RPC_URL; in local dev fall back to the chain's public RPC.
const RPC_URL = import.meta.env.VITE_RPC_URL as string | undefined;
function siteTransport() {
  if (RPC_URL) return fallback([http(RPC_URL), http()]);
  if (typeof window !== "undefined" && !import.meta.env.DEV) {
    // /rpc proxy first (reliable when ROBINHOOD_RPC_URL is set on the server),
    // public RPC as a backstop so reads never fully die if /rpc isn't wired up.
    return fallback([http(`${window.location.origin}/rpc`), http()]);
  }
  return http();
}

export const config = createConfig({
  chains: [activeChain],
  connectors: [
    // Browser extensions / in-app browsers (MetaMask, Rabby, Robinhood extension…)
    injected({ shimDisconnect: true }),
    coinbaseWallet({ appName: appMetadata.name, appLogoUrl: appMetadata.icons[0] }),
    ...(WC_PROJECT_ID
      ? [
          walletConnect({
            projectId: WC_PROJECT_ID,
            metadata: appMetadata,
            showQrModal: true,
          }),
        ]
      : []),
  ],
  transports: {
    [robinhood.id]: siteTransport(),
    [robinhoodTestnet.id]: siteTransport(),
  },
});

export function explorerTx(hash: string) {
  return `${activeChain.blockExplorers.default.url}/tx/${hash}`;
}

export function explorerAddress(address: string) {
  return `${activeChain.blockExplorers.default.url}/address/${address}`;
}

export function shortAddress(address: string) {
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}
